import { useCallback, useEffect, useRef, useState } from 'react'

export type Phase = 'waiting' | 'betting' | 'locked' | 'revealing' | 'settled'

export type RoomInfo = { id: number; odds: number }
export type RoomStat = { id: number; players: number; total_bet: number }

export type MyBet = {
  roomId: number
  amount: number
  odds: number
}

export type YourResult = {
  roundId: string
  won: boolean
  payout: number
  roomId: number
  amount: number
  odds: number
  exploded: number[]
}

export type SettlementIssue = {
  roundId: string
  roomId: number
  status: 'reconciliation_required'
  code: string
}

export type Presence = {
  realPlayers: number
  bots: number
  totalBet: number
  watching: number
}

export type BlastSnap = {
  phase: Phase
  roundId: string | null
  tierId: string | null
  rooms: RoomInfo[]
  roomStats: RoomStat[]
  exploded: number[]
  bettingEndsIn: number | null
  explodeCount: number
  presence: Presence | null
  myBet: MyBet | null
  lastResult: YourResult | null
  settlementIssue: SettlementIssue | null
  balanceSyncRevision: number
  lastError: string | null
  connected: boolean
}

const initial: BlastSnap = {
  phase: 'waiting',
  roundId: null,
  tierId: null,
  rooms: [],
  roomStats: [],
  exploded: [],
  bettingEndsIn: null,
  explodeCount: 3,
  presence: null,
  myBet: null,
  lastResult: null,
  settlementIssue: null,
  balanceSyncRevision: 0,
  lastError: null,
  connected: false,
}

function wsURL(tier: string): string {
  const env = (import.meta.env.VITE_WS_URL as string | undefined) ?? ''
  const withTier = (raw: string) => {
    const u = new URL(raw, window.location.href)
    u.searchParams.delete('token')
    if (tier && tier !== 'classic_9') u.searchParams.set('tier', tier)
    else u.searchParams.delete('tier')
    return u.toString()
  }
  if (env) {
    const u = new URL(env)
    if (u.pathname.includes('/ws/crash')) u.pathname = '/ws/blast'
    if (!u.pathname.includes('/ws/')) u.pathname = '/ws/blast'
    return withTier(u.toString())
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return withTier(`${proto}//${window.location.host}/ws/blast`)
}

function openSocket(token: string, tier: string): WebSocket {
  const url = wsURL(tier)
  try {
    return new WebSocket(url, ['bearer', token])
  } catch {
    const u = new URL(url)
    u.searchParams.set('token', token)
    return new WebSocket(u.toString())
  }
}

function asData(raw: unknown): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

function num(v: unknown, fallback = 0): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function flag(v: unknown): boolean {
  return v === true || v === 'true' || v === 1
}

function sequenceValue(v: unknown): number | null {
  if (v == null || v === '') return null
  const value = Number(v)
  return Number.isSafeInteger(value) && value >= 0 ? value : null
}

function roundIdValue(data: Record<string, unknown>): string | null {
  if (data.round_id == null) return null
  const value = String(data.round_id)
  return value.length > 0 ? value : null
}

function parseRooms(v: unknown): RoomInfo[] {
  if (!Array.isArray(v)) return []
  return v
    .map((r) => {
      const o = r as Record<string, unknown>
      return { id: num(o.id), odds: num(o.odds) }
    })
    .filter((r) => r.id > 0)
}

function parseStats(v: unknown): RoomStat[] {
  if (!Array.isArray(v)) return []
  return v
    .map((r) => {
      const o = r as Record<string, unknown>
      return {
        id: num(o.id ?? o.room_id),
        players: num(o.players ?? o.player_count ?? o.count),
        total_bet: num(o.total_bet ?? o.totalBet),
      }
    })
    .filter((r) => r.id > 0)
}

function parseExploded(v: unknown): number[] {
  if (!Array.isArray(v)) return []
  return v.map((x) => num(x)).filter((x) => x > 0)
}

function parsePresence(v: unknown): Presence | null {
  if (v == null || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  return {
    realPlayers: num(o.real_players),
    bots: num(o.bots),
    totalBet: num(o.total_bet),
    watching: num(o.watching),
  }
}

function parseMyBet(v: unknown): MyBet | null {
  if (v == null || typeof v !== 'object') return null
  const data = v as Record<string, unknown>
  const roomId = num(data.room_id)
  const amount = num(data.amount)
  const odds = num(data.odds)
  if (roomId <= 0 || amount <= 0) return null
  return { roomId, amount, odds }
}

function parseMyResult(v: unknown): YourResult | null {
  if (v == null || typeof v !== 'object') return null
  const data = v as Record<string, unknown>
  const roundId = roundIdValue(data)
  const roomId = num(data.room_id)
  const amount = num(data.amount)
  if (roundId == null || roomId <= 0 || amount <= 0) return null
  return {
    roundId,
    won: flag(data.won),
    payout: num(data.payout),
    roomId,
    amount,
    odds: num(data.odds),
    exploded: parseExploded(data.exploded),
  }
}

function parseSettlementIssue(v: unknown): SettlementIssue | null {
  if (v == null || typeof v !== 'object') return null
  const data = v as Record<string, unknown>
  const roundId = roundIdValue(data)
  const roomId = num(data.room_id)
  const status = String(data.status ?? '')
  if (roundId == null || roomId <= 0 || status !== 'reconciliation_required') {
    return null
  }
  return {
    roundId,
    roomId,
    status,
    code: String(data.code ?? 'settlement_reconciliation'),
  }
}

export function useBlastSocket(token: string | null, tier = 'classic_9') {
  const [snap, setSnap] = useState<BlastSnap>(initial)
  const wsRef = useRef<WebSocket | null>(null)
  const retryRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const pingRef = useRef<number | null>(null)
  const endsAtRef = useRef<number | null>(null)
  const tickRef = useRef<number | null>(null)
  const generationRef = useRef(0)
  const balanceSyncRevisionRef = useRef(0)

  const clearTimers = () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    if (pingRef.current != null) window.clearInterval(pingRef.current)
    if (tickRef.current != null) window.clearInterval(tickRef.current)
    timerRef.current = null
    pingRef.current = null
    tickRef.current = null
  }

  useEffect(() => {
    const generation = generationRef.current + 1
    generationRef.current = generation
    if (!token) return
    let stopped = false
    let lastSequence = -1
    let currentRoundId: string | null = null
    let hasJoined = false
    let preJoinMessages: Array<{
      typ: string
      data: Record<string, unknown>
    }> = []
    retryRef.current = 0
    setSnap({
      ...initial,
      balanceSyncRevision: balanceSyncRevisionRef.current,
      connected: false,
    })

    const isCurrentGeneration = () =>
      !stopped && generationRef.current === generation

    const isCurrentSocket = (socket: WebSocket) =>
      isCurrentGeneration() && wsRef.current === socket

    const markBalanceDirty = () => {
      balanceSyncRevisionRef.current += 1
      return balanceSyncRevisionRef.current
    }

    const startCountdown = (endsAtMs: number) => {
      if (!isCurrentGeneration()) return
      endsAtRef.current = endsAtMs
      if (tickRef.current != null) window.clearInterval(tickRef.current)
      const tick = () => {
        if (!isCurrentGeneration()) return
        const end = endsAtRef.current
        if (end == null) return
        const rem = Math.max(0, (end - Date.now()) / 1000)
        setSnap((s) => ({ ...s, bettingEndsIn: rem }))
      }
      tick()
      tickRef.current = window.setInterval(tick, 100)
    }

    const handleMessage = (typ: string, data: Record<string, unknown>) => {
      if (!isCurrentGeneration()) return
      if (!hasJoined && typ !== 'joined') {
        // Hub broadcasts can be queued before the initial joined snapshot.
        // Preserve them until joined establishes the round so a one-off phase
        // transition (betting_closed/boom/round_result) cannot be lost.
        if (preJoinMessages.length >= 256) preJoinMessages.shift()
        preJoinMessages.push({ typ, data })
        return
      }
      const completesHandshake = !hasJoined && typ === 'joined'
      if (completesHandshake) hasJoined = true

      const sequence = sequenceValue(data.sequence)
      const staleSequence = sequence != null && sequence < lastSequence
      if (sequence != null) {
        if (staleSequence && typ !== 'joined') return
        if (sequence > lastSequence) lastSequence = sequence
      }

      const eventRoundId = roundIdValue(data)
      if (staleSequence && currentRoundId != null) {
        // A newer broadcast can reach this client before its joined
        // snapshot. Restore same-round private state and connection only;
        // never let the older snapshot roll public game state backwards.
        const myBet = parseMyBet(data.my_bet)
        const parsedResult = parseMyResult(data.my_result)
        const settlementIssue = parseSettlementIssue(data.my_settlement)
        const sameRound =
          eventRoundId != null &&
          (currentRoundId == null || eventRoundId === currentRoundId)
        const myResult =
          sameRound && parsedResult?.roundId === eventRoundId
            ? parsedResult
            : null
        const balanceSyncRevision =
          settlementIssue != null || (sameRound && (myBet != null || myResult != null))
            ? markBalanceDirty()
            : null
        setSnap((s) => ({
          ...s,
          connected: true,
          lastError: null,
          myBet: sameRound && myBet != null ? myBet : s.myBet,
          lastResult: myResult ?? s.lastResult,
          settlementIssue: settlementIssue ?? s.settlementIssue,
          balanceSyncRevision:
            balanceSyncRevision ?? s.balanceSyncRevision,
        }))
        return
      }

      // With no established round, a stale joined snapshot is still the only
      // source of complete public and private hydration.
      if (typ === 'joined') {
        currentRoundId = eventRoundId
      } else if (typ === 'round_start') {
        if (eventRoundId == null) return
        currentRoundId = eventRoundId
      } else if (
        eventRoundId != null &&
        eventRoundId !== currentRoundId &&
        typ !== 'settlement_error'
      ) {
        return
      }

      if (
        (typ === 'bet_ok' || typ === 'room_changed' || typ === 'your_result') &&
        (eventRoundId == null || eventRoundId !== currentRoundId)
      ) return

      applyMessage(
        typ,
        data,
        startCountdown,
        setSnap,
        markBalanceDirty,
        currentRoundId,
      )

      if (completesHandshake && preJoinMessages.length > 0) {
        const queued = preJoinMessages
        preJoinMessages = []
        queued.forEach((message) => handleMessage(message.typ, message.data))
      }
    }

    const connect = () => {
      if (!isCurrentGeneration() || !token) return
      clearTimers()
      // Every transport connection needs its own authoritative joined
      // handshake. Otherwise an event from a round missed while offline can
      // advance the watermark before joined and strand the UI on the old round.
      hasJoined = false
      preJoinMessages = []
      const sock = openSocket(token, tier)
      wsRef.current = sock

      sock.onopen = () => {
        if (!isCurrentSocket(sock)) return
        retryRef.current = 0
        // Stay fail-closed until the authoritative joined snapshot arrives.
        setSnap((s) => ({ ...s, connected: false, lastError: null }))
        pingRef.current = window.setInterval(() => {
          if (!isCurrentSocket(sock)) return
          if (sock.readyState === WebSocket.OPEN) {
            sock.send(JSON.stringify({ type: 'ping' }))
          }
        }, 20000)
      }

      sock.onclose = () => {
        if (!isCurrentSocket(sock)) return
        wsRef.current = null
        clearTimers()
        setSnap((s) => ({ ...s, connected: false }))
        const delay = Math.min(8000, 500 * 2 ** retryRef.current)
        retryRef.current += 1
        timerRef.current = window.setTimeout(() => {
          if (!isCurrentGeneration()) return
          connect()
        }, delay)
      }

      sock.onerror = () => {
        if (!isCurrentSocket(sock)) return
        /* onclose handles retry */
      }

      sock.onmessage = (ev) => {
        if (!isCurrentSocket(sock)) return
        let env: { type?: string; data?: unknown; payload?: unknown }
        try {
          env = JSON.parse(String(ev.data)) as typeof env
        } catch {
          return
        }
        const typ = env.type ?? ''
        const data = asData(env.data ?? env.payload)
        handleMessage(typ, data)
      }
    }

    connect()
    return () => {
      stopped = true
      if (generationRef.current === generation) {
        generationRef.current += 1
      }
      clearTimers()
      const sock = wsRef.current
      wsRef.current = null
      try {
        sock?.close()
      } catch {
        /* ignore */
      }
    }
  }, [token, tier])

  const send = useCallback((type: string, payload: Record<string, unknown>) => {
    const sock = wsRef.current
    if (!sock || sock.readyState !== WebSocket.OPEN) return false
    setSnap((current) =>
      current.lastError == null ? current : { ...current, lastError: null },
    )
    sock.send(JSON.stringify({ type, data: payload }))
    return true
  }, [])

  const placeBet = useCallback(
    (amount: number, roomId: number) => {
      return send('place_bet', { amount, room_id: roomId })
    },
    [send],
  )

  const changeRoom = useCallback(
    (roomId: number) => {
      return send('change_room', { room_id: roomId })
    },
    [send],
  )

  return { snap, placeBet, changeRoom }
}

function applyMessage(
  typ: string,
  data: Record<string, unknown>,
  startCountdown: (endsAtMs: number) => void,
  setSnap: (fn: (s: BlastSnap) => BlastSnap) => void,
  markBalanceDirty: () => number,
  currentRoundId: string | null,
) {
  switch (typ) {
    case 'joined':
    case 'round_start': {
      const rooms = parseRooms(data.rooms)
      const endsAt = num(data.betting_ends_at, 0)
      const myBet = typ === 'joined' ? parseMyBet(data.my_bet) : null
      const parsedResult = typ === 'joined' ? parseMyResult(data.my_result) : null
      const settlementIssue =
        typ === 'joined' ? parseSettlementIssue(data.my_settlement) : null
      const myResult =
        parsedResult?.roundId === currentRoundId ? parsedResult : null
      const balanceSyncRevision =
        typ === 'joined' &&
        (myBet != null || myResult != null || settlementIssue != null)
          ? markBalanceDirty()
          : null
      let phase: Phase = 'betting'
      if (typ === 'joined' && data.status) {
        const st = String(data.status)
        if (
          st === 'waiting' ||
          st === 'betting' ||
          st === 'locked' ||
          st === 'revealing' ||
          st === 'settled'
        ) {
          phase = st
        }
      } else if (typ === 'round_start') {
        phase = 'betting'
      }
      setSnap((s) => ({
        ...s,
        connected: true,
        phase,
        roundId: data.round_id != null ? String(data.round_id) : s.roundId,
        tierId: data.tier_id != null ? String(data.tier_id) : s.tierId,
        rooms: rooms.length ? rooms : s.rooms,
        roomStats: parseStats(data.room_stats ?? data.rooms),
        exploded:
          phase === 'revealing' || phase === 'settled'
            ? parseExploded(data.exploded)
            : [],
        explodeCount: num(data.explode_count, s.explodeCount) || 3,
        presence:
          parsePresence(data.presence) ?? (typ === 'round_start' ? null : s.presence),
        myBet,
        lastResult: myResult,
        settlementIssue: typ === 'joined' ? settlementIssue : s.settlementIssue,
        balanceSyncRevision: balanceSyncRevision ?? s.balanceSyncRevision,
        lastError: null,
      }))
      if (endsAt > 0 && phase === 'betting') {
        startCountdown(endsAt)
      }
      break
    }
    case 'betting_closed':
      setSnap((s) => ({
        ...s,
        phase: 'locked',
        bettingEndsIn: 0,
      }))
      break
    case 'boom':
      setSnap((s) => ({
        ...s,
        phase: 'revealing',
        exploded: parseExploded(data.exploded),
      }))
      break
    case 'round_result':
      setSnap((s) => ({
        ...s,
        phase: 'settled',
        exploded: parseExploded(data.exploded),
      }))
      break
    case 'room_stats':
      setSnap((s) => ({
        ...s,
        roomStats: parseStats(data.rooms ?? data.room_stats),
      }))
      break
    case 'presence':
      setSnap((s) => ({
        ...s,
        presence: parsePresence(data) ?? s.presence,
      }))
      break
    case 'bet_ok': {
      const myBet = parseMyBet(data)
      if (myBet == null) break
      const balanceSyncRevision = markBalanceDirty()
      setSnap((s) => ({
        ...s,
        myBet,
        balanceSyncRevision,
        lastError: null,
      }))
      break
    }
    case 'room_changed':
      setSnap((s) => ({
        ...s,
        myBet: s.myBet
          ? {
              ...s.myBet,
              roomId: num(data.room_id),
              odds: num(data.odds),
            }
          : null,
        lastError: null,
      }))
      break
    case 'your_result': {
      const result = parseMyResult(data)
      if (result == null || result.roundId !== currentRoundId) break
      const balanceSyncRevision = markBalanceDirty()
      setSnap((s) => ({
        ...s,
        exploded: result.exploded.length ? result.exploded : s.exploded,
        lastResult: result,
        balanceSyncRevision,
      }))
      break
    }
    case 'settlement_error': {
      const settlementIssue = parseSettlementIssue(data)
      if (settlementIssue == null) break
      const balanceSyncRevision = markBalanceDirty()
      setSnap((s) => ({
        ...s,
        settlementIssue,
        balanceSyncRevision,
        lastError: null,
      }))
      break
    }
    case 'error': {
      const code = String(data.code ?? '')
      setSnap((s) => {
        const settlementIssue =
          code === 'settlement_reconciliation_required'
            ? s.settlementIssue ?? {
                roundId: '',
                roomId: 0,
                status: 'reconciliation_required' as const,
                code,
              }
            : s.settlementIssue
        return {
          ...s,
          settlementIssue,
          lastError:
            code === 'settlement_reconciliation_required'
              ? null
              : String(data.message || data.code || 'error'),
        }
      })
      break
    }
    default:
      break
  }
}
